import { DragDropContext, Droppable } from "react-beautiful-dnd";
import { useSelector } from "react-redux";

import {
  TableContainer,
  TableHead,
  Table,
  TableBody,
  TableRow,
} from "@mui/material";

import { ClearLines } from "components/EditPage/ClearLines";
import { LineTableHeadingCell } from "components/formFields/LineTableCell";
import { LineColumnFillHeading } from "components/formFields/line/LineColumnFillHeading";
import { LineColumnHeading } from "components/formFields/line/LineColumnHeading";
import { LineInventoryItemHeading } from "components/formFields/line/LineInventoryItem";
import { LineItemFooter } from "components/formFields/line/LineItemFooter";
import { DragHandleHeading } from "components/utility/DragHandle";
import { EditLineButtonHeading } from "components/utility/EditLineButton";

import { i18n } from "services/i18nService";
import { expandItemGroup } from "services/sosInventoryService/expandItemGroup";

import { useClasses } from "hooks/useClasses.js";
import { useJobWorkcenters } from "hooks/useJobWorkcenters";
import { useLineItemHandlers } from "hooks/useLineItemHandlers";

import { MemoizedLineItem } from "views/WorkOrders/WorkOrder/MemoizedLineItem";

import { EMPTY_LINE_ITEM } from "editConfig";

export function LineItems(props) {
  const {
    record,
    objectType,
    lines,
    items,
    totals,
    itemsCount,
    addItem,
    lineHandler,
    setClearLines,
    lineItemsErrors,
  } = props;

  const { classes, classesCount } = useClasses();
  const jobWorkcenters = useJobWorkcenters();

  const {
    readOnlyLineIds,
    setLineEdit,
    handleDeleteLine,
    insertEmptyLine,
    handleDrop,
  } = useLineItemHandlers(
    record?.id,
    lines,
    lineHandler,
    EMPTY_LINE_ITEM[objectType]
  );

  // SETTINGS
  // Work order wgt and vol use the sales setting instead of production
  const showVolumeOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showVolumeOnSales
  );
  const showWeightOnSales = useSelector(
    (state) => state.userCompanySettings.settings.showWeightOnSales
  );
  const classEnabled = useSelector(
    (state) => state.userCompanySettings.settings.classEnabled
  );
  const jobEnabled = useSelector(
    (state) => state.userCompanySettings.settings.jobEnabled
  );
  const uomEnabled = useSelector(
    (state) => state.userCompanySettings.settings.uomEnabled
  );

  if (!lines) {
    return null;
  }

  return (
    <TableContainer data-testing="lineItems" sx={{ overflowX: "initial" }}>
      <Table size="small" padding="none" stickyHeader>
        <TableHead>
          <TableRow sx={{ verticalAlign: "bottom", lineHeight: "1" }}>
            <DragHandleHeading />
            <EditLineButtonHeading recordId={record.id} />
            <ClearLines setClearLines={setClearLines} />
            <LineTableHeadingCell>{i18n("columns.Line")}</LineTableHeadingCell>
            <LineInventoryItemHeading
              label={i18n("columns.ItemToMake")}
              lineHandler={lineHandler}
            />
            <LineColumnHeading labelCode="Instructions" />
            <LineColumnHeading labelCode="Quantity" />
            {uomEnabled && <LineColumnHeading labelCode="UOM" />}
            {showWeightOnSales && <LineColumnHeading labelCode="Weight" />}
            {showVolumeOnSales && <LineColumnHeading labelCode="Volume" />}
            <LineColumnHeading labelCode="Produced" sx={{ minWidth: "7rem" }} />
            <LineColumnFillHeading
              field="duedate"
              labelCode="DueDate"
              lineHandler={lineHandler}
            />
            <LineColumnHeading labelCode="For" />
            {classEnabled && (
              <LineColumnFillHeading
                labelCode="Class"
                field="class"
                lineHandler={lineHandler}
              />
            )}
            {jobEnabled && (
              <LineColumnFillHeading
                labelCode="Job"
                field="jobWorkcenter"
                lineHandler={lineHandler}
              />
            )}
          </TableRow>
        </TableHead>
        <DragDropContext onDragEnd={handleDrop}>
          <Droppable droppableId="lineItems">
            {(provided) => (
              <TableBody ref={provided.innerRef} {...provided.droppableProps}>
                {lines.map((line, index) => {
                  const lineReadOnly = readOnlyLineIds.has(index);

                  return (
                    <MemoizedLineItem
                      key={line.id || `temp-${index}`}
                      index={index}
                      objectType={objectType}
                      record={record}
                      line={line}
                      lineHandler={lineHandler}
                      expandItemGroup={(line) =>
                        expandItemGroup(line, lineHandler, objectType, record)
                      }
                      items={items}
                      itemsCount={itemsCount}
                      classes={classes}
                      classesCount={classesCount}
                      addItem={addItem}
                      jobWorkcenters={jobWorkcenters}
                      lineItemsErrors={lineItemsErrors}
                      insertEmptyLine={insertEmptyLine}
                      isLastLine={line.lineNumber === lines.length}
                      setLineEdit={setLineEdit}
                      lineReadOnly={lineReadOnly}
                      nextLineReadOnly={readOnlyLineIds.has(index + 1)}
                      handleDeleteLine={handleDeleteLine}
                    />
                  );
                })}
                {provided.placeholder}
              </TableBody>
            )}
          </Droppable>
        </DragDropContext>
        <LineItemFooter
          showWeight={showWeightOnSales}
          showVolume={showVolumeOnSales}
          totals={totals}
          firstCellSpan={5}
          lineHandler={lineHandler}
          objectType={objectType}
          recordId={record.id}
        />
      </Table>
    </TableContainer>
  );
}
